/**
 * Profile Avatar Component
 * Displays user profile image with fallback to user icon
 */

import React from 'react';

import './ProfileAvatar.css';

interface ProfileAvatarProps {
  imageUrl?: string | null;
  name: string;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  showInitials?: boolean;
}

// Default user icon SVG component
const UserIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const getInitials = (displayName: string): string => displayName
  .split(' ')
  .map((n) => n[0])
  .join('')
  .toUpperCase()
  .slice(0, 2);

const renderImage = (imageUrl: string, name: string, sizeClass: string, className: string) => (
  <img src={imageUrl} alt={name} className={`profile-avatar ${sizeClass} ${className}`} />
);

const renderInitials = (name: string, sizeClass: string, className: string) => (
  <div className={`profile-avatar initials ${sizeClass} ${className}`}>{getInitials(name)}</div>
);

const renderUserIcon = (sizeClass: string, className: string) => (
  <div className={`profile-avatar user-icon ${sizeClass} ${className}`}>
    <UserIcon className="avatar-icon" />
  </div>
);

const selectAvatarRenderer = (
  imageUrl: string | null | undefined,
  name: string,
  showInitials: boolean,
  sizeClass: string,
  className: string,
) => {
  if (imageUrl) return renderImage(imageUrl, name, sizeClass, className);
  if (showInitials && name) return renderInitials(name, sizeClass, className);

  return renderUserIcon(sizeClass, className);
};

export const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  imageUrl,
  name,
  size = 'small',
  className = '',
  showInitials = false,
}) => {
  const sizeClass = `avatar-${size}`;

  return selectAvatarRenderer(imageUrl, name, showInitials, sizeClass, className);
};
